import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { EyeIcon, EyeOff } from "lucide-react";
import { fetchData } from "../../../utils/functions";
import {
  ThinDropdown,
  ThinTextField,
  NICInputField,
  PhoneInputField,
  BackButton,
  ThinDropdownWithSearch,
  SubmitButton,
  ThinTextarea,
} from "../../common/GeneralComponent";
import Select from "react-select";

const AddUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [groupsData, setGroupsData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [groupId, setGroupId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [villages, setVillages] = useState([]);
  const [supervisorDistricts, setSupervisorDistricts] = useState([]);

  const validationSchema = Yup.object({
    groupId: Yup.number().required("Group is required"),
    name: Yup.string()
      .required("Name is required")
      .min(3, "Name must be at least 3 characters long")
      .max(50, "Name must be at most 50 characters long")
      .matches(/^[^\d]/, "Name cannot start with a number")
      .matches(/^[A-Za-z\s]+$/, "Name can only contain alphabets"),
  cnic: Yup.string()
  .nullable()
  .test('cnic-test', function(value) {
    // If empty, it's valid (nullable)
    if (!value || value.trim() === '') {
      return true;
    }
    
    // Check format
    if (!/^\d{5}-\d{7}-\d$/.test(value)) {
      return this.createError({
        message: 'Please enter a valid CNIC'
      });
    }
    
    return true;
  }),
    phone_number: Yup.string()
      .matches(
        /^03\d{9}$/,
        "Phone number must start with 03 and be 11 digits long"
      )
      .required("Phone number is required"),
    // email: Yup.string()
    //   .transform((v) => (typeof v === "string" ? v.trim() : v))
    //   .required("Email is required")
    //   .email("Email format is invalid"),
 // Update the email validation in your Yup schema - replace the email part with:
   email: Yup.string()
  .transform((v) => (typeof v === "string" ? v.trim().toLowerCase() : v))
  .required("Email is required")
  .matches(
    /^(?!.*\.\.)(?!\.)[a-z0-9][a-z0-9._%+-]*[a-z0-9]@[a-z0-9.-]+\.[a-z]{2,}$/,
    "Email format is invalid (valid formats: user@gmail.com, name@domain.com, first.last@company.co.uk)"
  )
  .test(
    "valid-email-structure",
    "Email format is invalid (valid formats: user@gmail.com, name@domain.com, first.last@company.co.uk)",
    (value) => {
      if (!value) return false;
      
      // Basic structure check
      if (!value.includes('@') || value.startsWith('@') || value.endsWith('@')) {
        return false;
      }
      
      const parts = value.split('@');
      if (parts.length !== 2) return false;
      
      const [localPart, domainPart] = parts;
      
      // Local part validation
      if (!localPart || localPart.length === 0) return false;
      if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
      if (localPart.includes('..')) return false;
      if (/[^a-z0-9._%+-]/.test(localPart)) return false;
      
      // Domain part validation
      if (!domainPart || domainPart.length === 0) return false;
      if (domainPart.startsWith('.') || domainPart.endsWith('.')) return false;
      if (domainPart.includes('..')) return false;
      if (!domainPart.includes('.')) return false;
      
      const domainParts = domainPart.split('.');
      const tld = domainParts[domainParts.length - 1];
      
      // TLD validation
      if (tld.length < 2) return false;
      if (!/^[a-z]+$/.test(tld)) return false;
      
      // Main domain validation (part before TLD)
      const mainDomain = domainParts.slice(0, -1).join('.');
      if (!mainDomain || mainDomain.length === 0) return false;
      
      return true;
    }
  ),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long")
      .max(15, "Password must be at most 15 characters long")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[\W_]/, "Password must contain at least one special character"),
    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf(
        [Yup.ref("password")],
        "Confirm Password should be same as Password"
      ),
    roleId: Yup.number().required("Role is required"),
    status: Yup.number().required("Status is required"),
    supervisor_districts: Yup.array().when("roleId", {
      is: (roleId) => roleId && Number(roleId) === 3,
      then: (schema) =>
        schema.min(1, "At least one district must be assigned for supervisors"),
      otherwise: (schema) => schema,
    }),
    enumerator_villages: Yup.array().when("roleId", {
      is: (roleId) => roleId && Number(roleId) === 4,
      then: (schema) => schema,
      otherwise: (schema) => schema,
    }),

    selected_district: Yup.string().when("roleId", {
      is: (roleId) => roleId && Number(roleId) === 4,
      then: (schema) => schema.required("District selection is required"),
      otherwise: (schema) => schema,
    }),

    organization: Yup.string().when("roleId", {
      is: () =>
        [
          "project staff",
          "project technical staff",
          "partner staff",
          "partner technical staff",
        ].includes(roleName),
      then: (schema) => schema.required("Organization is required"),
      otherwise: (schema) => schema,
    }),
    partner_org_name: Yup.string().when("organization", {
      is: (org) => org && org !== "",
      then: (schema) =>
        schema.required("Partner Organization Name is required"),
      otherwise: (schema) => schema,
    }),
    other_organization: Yup.string().when("organization", {
      is: "Other",
      then: (schema) => schema.required("Please specify other organization"),
      otherwise: (schema) => schema,
    }),
  });

  // const handleEmailChange = (e) => {
  //   const value = e.target.value.toLowerCase();
  //   // Update your form state with the lowercase value
  //   setFieldValue("email", value);
  // };

  const formik = useFormik({
    initialValues: {
      groupId: "",
      name: "",
      email: "",
      supervisor_id: "",
      password: "",
      cnic: "",
      phone_number: "",
      roleId: "",
      confirmPassword: "",
      designation: "",
      status: 1,
      user_code: "",
      supervisor_districts: [],
      enumerator_villages: [],
      organization: "",
      partner_org_name: "",
      other_organization: "",
      selected_districts: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      const selected = roles.find((r) => r.role.id === values.roleId);
      const roleName = selected?.role?.role_name?.trim().toLowerCase();

      if (values.roleId) {
        formik.setFieldValue("roleId", Number(values.roleId));
      }

      if (roleName === "enumerator" && !values.supervisor_id) {
        toast.error("Supervisor is required for enumerators.");
        return;
      }

      if (roleName === "enumerator" && !values.selected_districts) {
        toast.error("District selection is required for enumerators.");
        return;
      }

      try {
        const payload = {
          name: values.name,
          email: values.email,
          password: values.password,
          cnic: values.cnic,
          phone_number: values.phone_number,
          roleId: Number(values.roleId),
          status: Number(values.status),
          groupId: groupId,
          user_code: values.user_code || null,
          supervisor_id: values.supervisor_id
            ? Number(values.supervisor_id)
            : null,
          designation: values.designation || null,
          supervisor_districts: values.supervisor_districts || [],
          enumerator_districts: values.selected_districts || [],
          // enumerator_districts:
          //   Number(values.roleId) === 2
          //     ? values.selected_districts
          //       ? [Number(values.selected_districts)]
          //       : []
          //     : values.supervisor_districts || [],
          enumerator_villages: values.enumerator_villages || [],
          organization: values.organization || null,
          partner_organization_name: values.partner_org_name || null,
          other_specify: values.other_organization || null,
        };

        await fetchData("user", "POST", payload);
        toast.success("User created successfully");
        formik.resetForm();
        setSupervisorDistricts([]);
        setVillages([]);
        setIsSubmitting(true);
        navigate("/users");
      } catch (error) {
        toast.error(error.message || "Failed to create user.");
      }
    },
  });

  const selectedRole = roles.find((r) => r.role.id == formik.values.roleId);
  const roleName = selectedRole?.role?.role_name?.trim().toLowerCase();

  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        const response = await fetchData(
          "user",
          "GET",
          null,
          {},
          {
            search: "active",
            role: "supervisor",
            groupFilter: groupId,
            limit: 1000,
          },
          navigate,
          "/user",
          dispatch
        );
        const onlySupervisors = (response?.data || []).filter(
          (item) => item.role_name?.toLowerCase() === "supervisor"
        );
        setSupervisors(onlySupervisors);
      } catch {
        toast.error("Failed to fetch supervisors.");
      }
    };
    if (roleName === "enumerator" && groupId) {
      fetchSupervisors();
    } else {
      setSupervisors([]);
    }
  }, [roleName, groupId, dispatch, navigate]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetchData(
          "user-form-options",
          "GET",
          null,
          {},
          {},
          navigate,
          "/user-form-options",
          dispatch
        );
        setGroupsData(response?.data || []);
      } catch {
        toast.error("Failed to load groups.");
      }
    };
    fetchGroups();
  }, [dispatch, navigate]);

  useEffect(() => {
    const group = groupsData.find((g) => Number(g.id) === Number(groupId));
    setRoles(group?.roles || []);
  }, [groupId, groupsData]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const resp = await fetchData(
          "districts",
          "GET",
          {},
          {},
          {},
          navigate,
          "/districts",
          dispatch
        );
        setDistricts(resp?.data || []);
      } catch {
        toast.error("Failed to fetch districts.");
      }
    };
    fetchDistricts();
  }, [dispatch, navigate]);
  useEffect(() => {
    if (roleName !== "enumerator") {
      setSupervisorDistricts([]);
      setVillages([]);
      formik.setFieldValue("selected_district", "");
      return;
    }

    const supId = formik.values.supervisor_id;
    if (!supId) {
      setSupervisorDistricts([]);
      setVillages([]);
      formik.setFieldValue("selected_district", "");
      return;
    }

    const selectedSupervisor = supervisors.find((s) => s.id == supId);
    const assigned = selectedSupervisor?.supervisor_districts || [];

    if (!selectedSupervisor || assigned.length === 0) {
      toast.error("Please assign district(s) to this supervisor first.");
      setSupervisorDistricts([]);
      setVillages([]);
      formik.setValues((prev) => ({
        ...prev,
        supervisor_id: "",
        selected_district: "",
      }));
      return;
    }
    const filtered = districts.filter((d) => assigned.includes(d.district_id));
    setSupervisorDistricts(filtered);

    const hasDistrictSelection =
      Array.isArray(formik.values.selected_districts) &&
      formik.values.selected_districts.length > 0;

    const resetDistricts = !hasDistrictSelection && filtered.length > 0;
    formik.setValues((prev) => ({
      ...prev,
      ...(resetDistricts && {
        selected_districts:
          filtered.length === 1 ? [filtered[0].district_id] : [],
      }),
      enumerator_villages: [],
    }));
    setVillages([]);
  }, [formik.values.supervisor_id, roleName, supervisors, districts]);

 useEffect(() => {
   const fetchVillages = async () => {
     const districtIds = formik.values.selected_districts;
     if (!districtIds || districtIds.length === 0) {
       setVillages([]);
       formik.setFieldValue("enumerator_villages", []);
       return;
     }

     try {
       const filters = { district: `[${districtIds}]` };
       const resp = await fetchData(
         "villages",
         "GET",
         {},
         {},
         filters,
         navigate,
         "/villages",
         dispatch
       );

       if (resp?.success === false) {
         toast.error(resp.message || "Failed to fetch villages.");
         setVillages([]);
       } else {
         setVillages(resp?.data || []);
       }
     } catch {
       toast.error("Failed to fetch villages.");
       setVillages([]);
     }
   };

   fetchVillages();
 }, [formik.values.selected_districts, dispatch, navigate]);
  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex justify-between items-center w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-bold">
          Create User
        </h1>
        <BackButton to="/users" text="Back" />
      </div>

      <div className="bg-white rounded-lg shadow mt-4 mb-12">
        <div className="p-4 border-b gradiant2 rounded-t-lg">
          <h1 className="text-sm tracking-wider text-[#856800] font-bold uppercase">
            Create Users
          </h1>
        </div>

        <form autoComplete="off" className="p-4" onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="mt-3">
              <ThinDropdownWithSearch
                label="Group"
                options={groupsData.map((grp) => ({
                  value: grp.id,
                  label: grp.name,
                }))}
                value={groupId || ""}
                onChange={(newValue) => {
                  setGroupId(newValue);
                  setRoles([]);
                  setSupervisors([]);
                  formik.setValues((prev) => ({
                    ...prev,
                    groupId: newValue,
                    roleId: "",
                    supervisor_id: "",
                  }));
                }}
                error={formik.touched.groupId && formik.errors.groupId}
              />
              {formik.touched.groupId && formik.errors.groupId && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.groupId}
                </div>
              )}
            </div>

            <div className="mt-3">
              <ThinDropdownWithSearch
                label="Role"
                options={roles.map((role) => ({
                  value: role.role.id,
                  label: role.role.role_name,
                }))}
                value={formik.values.roleId}
                onChange={(e) => {
                  formik.setValues((prev) => ({
                    ...prev,
                    roleId: e,
                    supervisor_id: "",
                    user_code: "",
                  }));
                }}
                error={formik.touched.roleId && formik.errors.roleId}
              />
              {formik.touched.roleId && formik.errors.roleId && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.roleId}
                </div>
              )}
            </div>

            <div className="mt-2">
              {/* Status */}
              <ThinDropdown
                label="Status"
                options={[
                  { value: 1, label: "Active" },
                  { value: 0, label: "Inactive" },
                ]}
                value={formik.values.status}
                onChange={(e) => formik.setFieldValue("status", e.target.value)}
                error={formik.touched.status && formik.errors.status}
              />
              {formik.touched.status && formik.errors.status && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.status}
                </div>
              )}
            </div>
            <div className="mt-3">
              <ThinTextField
                id="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && formik.errors.name}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.name}
                </div>
              )}
            </div>

            {/* {roleName === "enumerator" && (
              <div className="mt-3">
                <ThinTextField
                  id="user_code"
                  label="Enumerator Code"
                  value={formik.values.user_code}
                  onChange={formik.handleChange}
                  error={formik.touched.user_code && formik.errors.user_code}
                />
              </div>
            )} */}

            {roleName === "enumerator" && (
              <div className="mt-3">
                <ThinDropdownWithSearch
                  label="Supervisor"
                  options={supervisors.map((sup) => ({
                    value: sup.id,
                    label: sup.name,
                  }))}
                  value={formik.values.supervisor_id}
                  onChange={(selectedSupervisorId) => {
                    formik.setValues((prev) => ({
                      ...prev,
                      supervisor_id: selectedSupervisorId,
                      enumerator_villages: [],
                      selected_districts: [],
                    }));
                    setVillages([]);
                    setSupervisorDistricts([]);
                  }}
                  error={
                    formik.touched.supervisor_id && formik.errors.supervisor_id
                  }
                />
                {formik.touched.supervisor_id &&
                  formik.errors.supervisor_id && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.supervisor_id}
                    </div>
                  )}
              </div>
            )}
            {roleName === "enumerator" && (
              <>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Districts <span className="text-red-500">*</span>
                  </label>

                  {/* <Select
                    isMulti={true}
                    placeholder={
                      supervisorDistricts.length > 1
                        ? "Select District(s)"
                        : supervisorDistricts.length === 1
                        ? supervisorDistricts[0].district_name
                        : "No District Assigned"
                    }
                    options={supervisorDistricts.map((d) => ({
                      value: d.district_id,
                      label: d.district_name,
                    }))}
                    value={(formik.values.selected_districts || [])
                      .map((id) => {
                        const district = supervisorDistricts.find(
                          (d) => d.district_id === id
                        );
                        return district
                          ? {
                              value: district.district_id,
                              label: district.district_name,
                            }
                          : null;
                      })
                      .filter(Boolean)}
                    onChange={(selected) => {
                      const ids = selected ? selected.map((s) => s.value) : [];

                      formik.setValues((prev) => ({
                        ...prev,
                        selected_districts: ids,
                        enumerator_villages: [],
                      }));
                      setVillages([]);
                    }}
                  /> */}
                  <Select
                    isMulti
                    placeholder={
                      supervisorDistricts.length > 1
                        ? "Select District(s)"
                        : supervisorDistricts.length === 1
                        ? supervisorDistricts[0].district_name
                        : "No District Assigned"
                    }
                    options={supervisorDistricts.map((d) => ({
                      value: d.district_id,
                      label: d.district_name,
                    }))}
                    value={(formik.values.selected_districts || [])
                      .map((id) => {
                        const district = supervisorDistricts.find(
                          (d) => d.district_id === id
                        );
                        return district
                          ? {
                              value: district.district_id,
                              label: district.district_name,
                            }
                          : null;
                      })
                      .filter(Boolean)}
                    onChange={(selected) => {
                      const ids = selected ? selected.map((s) => s.value) : [];
                      formik.setFieldValue("selected_districts", ids);
                      // Clear villages whenever districts change
                      formik.setFieldValue("enumerator_villages", []);
                      setVillages([]);
                    }}
                  />

                  {formik.touched.selected_districts &&
                    formik.errors.selected_districts && (
                      <div className="text-red-500 text-sm mt-1">
                        {formik.errors.selected_districts}
                      </div>
                    )}
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <label className="mb-1 block text-sm font-medium">
                      Assign Villages
                    </label>
                    Total Villages {villages.length}
                    <button
                      type="button"
                      className="text-blue-600 text-xs underline hover:text-blue-800"
                      onClick={() => {
                        formik.setValues((prev) => ({
                          ...prev,
                          supervisor_id: "",
                          selected_districts: [],
                          enumerator_villages: [],
                        }));
                        setSupervisorDistricts([]);

                        setVillages([]);
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                  <Select
                    isMulti
                    options={villages.map((v) => ({
                      value: v.village_id,
                      label: v.village_name,
                    }))}
                    value={(formik.values.enumerator_villages || [])
                      .map((id) => {
                        const village = villages.find(
                          (v) => v.village_id === id
                        );
                        return village
                          ? {
                              value: village.village_id,
                              label: village.village_name,
                            }
                          : null;
                      })
                      .filter(Boolean)}
                    onChange={(selected) =>
                      formik.setFieldValue(
                        "enumerator_villages",
                        selected ? selected.map((s) => s.value) : []
                      )
                    }
                  />
                  {formik.touched.enumerator_villages &&
                    formik.errors.enumerator_villages && (
                      <div className="text-red-500 text-sm mt-1">
                        {formik.errors.enumerator_villages}
                      </div>
                    )}
                </div>
              </>
            )}

            {roleName === "supervisor" && (
              <div className="mt-3">
                <ThinTextField
                  id="user_code"
                  label="Supervisor Code"
                  value={formik.values.user_code}
                  onChange={formik.handleChange}
                  error={formik.touched.user_code && formik.errors.user_code}
                />
              </div>
            )}
            {roleName === "supervisor" && (
              <div className="mt-3  ">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District <span className="text-red-500">*</span>
                </label>
                <Select
                  isMulti
                  options={districts.map((d) => ({
                    value: d.district_id,
                    label: d.district_name,
                  }))}
                  value={(formik.values.supervisor_districts || [])
                    .map((id) => {
                      const district = districts.find(
                        (d) => d.district_id === id
                      );
                      return district
                        ? {
                            value: district.district_id,
                            label: district.district_name,
                          }
                        : null;
                    })
                    .filter(Boolean)}
                  onChange={(selected) =>
                    formik.setFieldValue(
                      "supervisor_districts",
                      selected ? selected.map((s) => s.value) : []
                    )
                  }
                />
                {formik.touched.supervisor_districts &&
                  formik.errors.supervisor_districts && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.supervisor_districts}
                    </div>
                  )}
              </div>
            )}
            {[
              "project staff",
              "project technical staff",
              "partner staff",
              "partner technical staff",
            ].includes(roleName) && (
              <>
                <div className="mt-0.5">
                  <ThinDropdown
                    label="Organization"
                    options={[
                      { value: "CRS", label: "CRS" },
                      { value: "SPHF", label: "SPHF" },
                      { value: "Govt", label: "Govt" },
                      { value: "SHRRP", label: "SHRRP" },
                      {
                        value: "Implementing Partner",
                        label: "Implementing Partner",
                      },
                      { value: "Other", label: "Other" },
                    ]}
                    value={formik.values.organization}
                    onChange={(e) =>
                      formik.setFieldValue("organization", e.target.value)
                    }
                    error={
                      formik.touched.organization && formik.errors.organization
                    }
                  />
                  {formik.touched.organization &&
                    formik.errors.organization && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.organization}
                      </div>
                    )}
                </div>
                {formik.values.organization === "Other" && (
                  <div className="mt-3">
                    <ThinTextField
                      id="other_organization"
                      label="Other Specify"
                      value={formik.values.other_organization}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.other_organization &&
                        formik.errors.other_organization
                      }
                    />
                    {formik.touched.other_organization &&
                      formik.errors.other_organization && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.other_organization}
                        </div>
                      )}
                  </div>
                )}

                <div className="mt-3">
                  <ThinTextField
                    id="partner_org_name"
                    label="Partner Organization Name"
                    value={formik.values.partner_org_name}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.partner_org_name &&
                      formik.errors.partner_org_name
                    }
                  />
                  {formik.touched.partner_org_name &&
                    formik.errors.partner_org_name && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.partner_org_name}
                      </div>
                    )}
                </div>
              </>
            )}

            {/* <div className="mt-3">
              <ThinTextField
                id="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim();
                  if (trimmed !== e.target.value) {
                    formik.setFieldValue("email", trimmed, false);
                  }
                  formik.handleBlur(e);
                }}
                error={formik.touched.email && formik.errors.email}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.email}
                </div>
              )}
            </div> */}

            <div className="mt-3">
              <ThinTextField
                id="email"
                label="Email"
                value={formik.values.email}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  formik.setFieldValue("email", value);
                }}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim().toLowerCase();
                  if (trimmed !== e.target.value) {
                    formik.setFieldValue("email", trimmed, false);
                  }
                  formik.handleBlur(e);
                }}
                error={formik.touched.email && formik.errors.email}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.email}
                </div>
              )}
            </div>
           <div className="mt-0">
              <NICInputField
                value={formik.values.cnic}
                onChange={(value) => formik.setFieldValue("cnic", value)}
                label="CNIC"
                error={formik.touched.cnic && formik.errors.cnic}
              />
              {/* Add this error display */}
              {formik.touched.cnic && formik.errors.cnic && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.cnic}
                </div>
              )}
            </div>
            <div className="relative">
              <PhoneInputField
                value={formik.values.phone_number}
                onChange={(value) =>
                  formik.setFieldValue("phone_number", value)
                }
                label="Phone Number"
                error={
                  formik.touched.phone_number && formik.errors.phone_number
                }
              />
              {formik.touched.phone_number && formik.errors.phone_number && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.phone_number}
                </div>
              )}
            </div>
            <div className="relative mt-3">
              <ThinTextField
                id="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && formik.errors.password}
              />
              <button
                type="button"
                className="absolute right-3 top-11 -translate-y-1/2 text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeIcon size={16} /> : <EyeOff size={16} />}
              </button>
              {formik.touched.password && formik.errors.password && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.password}
                </div>
              )}
            </div>

            <div className="relative mt-3">
              <ThinTextField
                id="confirmPassword"
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
              />
              <button
                type="button"
                className="absolute right-3 top-11 -translate-y-1/2 text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeIcon size={16} /> : <EyeOff size={16} />}
              </button>
              {formik.touched.confirmPassword &&
                formik.errors.confirmPassword && (
                  <div className="text-red-500 text-xs mt-1">
                    {formik.errors.confirmPassword}
                  </div>
                )}
            </div>
            <div className="relative"></div>
          </div>
          <div className="mt-6 space-y-1">
            <p className="text-red-600 text-xs">
              Password must be at least 8 characters long
            </p>
            <p className="text-red-600 text-xs">
              Password can be maximum 15 characters long
            </p>
            <p className="text-red-600 text-xs">
              Password must contain at least 1 uppercase, 1 lowercase, 1 number,
              and 1 special character
            </p>
          </div>
          <SubmitButton type="submit" text="Submit" isLoading={isSubmitting} />
        </form>
      </div>
    </div>
  );
};

export default AddUser;
